import { reportError } from './plainErrors';

/**
 * What the header's feedback slot says about the last controller command.
 *
 * The slot is the one place the console tells an operator that the room did, or did not, do what they
 * asked. It therefore has to hear about EVERY command, on every transport — and that is what this
 * module exists to make true. It used to be four literal objects and two magic timeouts scattered
 * through the provider, and the cloud path had them while the venue-network and Bluetooth path had
 * none: a controller that refused a command over a direct connection produced an empty header and a
 * card that simply never went Running.
 *
 * The refusal that made this visible: with the room listening on its own music input, the daemon
 * answers `start_audio_reactive` with `invalid-argument … "portaudio:ALSA:USB AUDIO  CODEC:
 * Audio (hw:2,0):2ch" is not available` (2026-09-15, the 1.7.16 post-OTA proof, F1). The operator saw
 * nothing they could act on.
 *
 * The daemon's own words are NOT what appears here. `plainErrors` owns that boundary — one plain
 * sentence for the person, the raw `code: message` to `console.error` for whoever is debugging — and
 * every failure in this module goes through it, so no transport can leak a protocol string into the
 * header by taking a different route.
 */
export interface ControlFeedback {
  command: string;
  phase: 'pending' | 'applied' | 'failed';
  /** The plain sentence a person reads. Only a failure carries one. */
  message?: string;
}

/** A feedback state, and how long it stays before the slot empties. `0` stays until it is replaced. */
export interface ControlFeedbackStep {
  feedback: ControlFeedback;
  clearAfter: number;
}

/** A refusal always carries its sentence, so a caller that also toasts it never has to check. */
export interface ControlFailureStep extends ControlFeedbackStep {
  feedback: ControlFeedback & { phase: 'failed'; message: string };
}

/** Long enough to read "Applied", short enough that the header is not carrying stale news. */
export const APPLIED_CLEAR_MS = 1_800;
/** A refusal is the one thing here worth re-reading, so it outstays a success. */
export const FAILED_CLEAR_MS = 4_000;

/** The command is on its way. It stays until its outcome replaces it, however long that takes. */
export function commandSent(command: string): ControlFeedbackStep {
  return { feedback: { command, phase: 'pending' }, clearAfter: 0 };
}

/** The controller did it. */
export function commandApplied(command: string): ControlFeedbackStep {
  return { feedback: { command, phase: 'applied' }, clearAfter: APPLIED_CLEAR_MS };
}

/**
 * The controller — or the relay, or the network — refused it, in plain language.
 *
 * `reportError` is what turns the daemon's `code: message` into that sentence and puts the raw text in
 * the browser console. A refusal this console has no mapped words for still reaches the operator as the
 * generic room failure rather than as silence.
 */
export function commandFailed(command: string, error: unknown): ControlFailureStep {
  return {
    feedback: { command, phase: 'failed', message: reportError(command, error).message },
    clearAfter: FAILED_CLEAR_MS,
  };
}
